import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

export interface ProductDetailsListStyles {
    wrapper?: InjectableCss;
    productDetailsTittle?: InjectableCss;
    descriptionAccordion?: InjectableCss;
    list?: InjectableCss;
    listContainer?: InjectableCss;
    listDecription?: InjectableCss;
}

export const productDetailsListStyles: ProductDetailsListStyles = {
    wrapper: {
        css: css`
            margin: 20px 0px;
        `,
    },
    productDetailsTittle: {
        css: css`
            border-bottom: 0.2rem solid ${getColor("primary")};
            box-sizing: border-box;
            display: inline-flex;
            font-weight: 600;
             color: "text.main";
            font-size: 24px;
            text-transform: uppercase;
        `,
    },
    descriptionAccordion: {
        css: css`
            margin-bottom: 20px;
            border-top: 1px solid  ${getColor("common.border")};
        `,
    },
    list: {
        css: css`
            margin-top: 10px;
            > ul {
                list-style: disc !important;
                padding-left: 20px;
                color: "text.main";
            }
        `,
    },
    listContainer: {
        css: css`
            padding: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 10px;
                    `,
                    css`
                        padding: 10px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    listDecription: {
        css: css`
            color: "text.main";
        `,
    },
};

const styles = productDetailsListStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasParentProductId & HasProduct;

const mapStateToProps = (state: ApplicationState, ownProps: HasParentProductId) => ({
    language: state.context.session.language?.languageCode,
});

const ProductDetailsList: React.FC<Props> = ({ product, fields, language }) => {
    if (
        !product ||
        !product.properties ||
        (typeof product.properties.ecoM_WEB_LONG_DESCRIPTION !== "string" &&
            typeof product.properties.ecoM_BULLET_LIST !== "string")
    ) {
        return null;
    }

    const [productList, setProductList] = React.useState([]);
    const [longDescription, setLongDescription] = React.useState("");

    React.useEffect(() => {
        const isFrench = language === "fr-CA";

        // Bullet list
        const bulletList = isFrench
            ? product?.properties?.frEcomBulletList || product?.properties?.ecoM_BULLET_LIST
            : product?.properties?.ecoM_BULLET_LIST;

        if (bulletList) {
            try {
                setProductList(JSON.parse(bulletList));
            } catch (e) {
                setProductList([]);
            }
        } else {
            setProductList([]);
        }

        // Long description
        const description = isFrench
            ? product?.properties?.frEcomWebLongDescription || product?.properties?.ecoM_WEB_LONG_DESCRIPTION
            : product?.properties?.ecoM_WEB_LONG_DESCRIPTION;

        setLongDescription(description || "");
    }, [product, language]);

    return (
        <>
            <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_specification">
                <StyledWrapper {...styles.descriptionAccordion}>
                    <StyledWrapper>
                        <Typography {...styles.productDetailsTittle}>
                            {fields.title ? fields.title : translate("PRODUCT DETAILS")}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.listContainer}>
                        <StyledWrapper {...styles.listDecription}>
                            {longDescription}
                        </StyledWrapper>
                        {productList.length > 0 && 
                            <StyledWrapper {...styles.list}>
                                {productList?.map((list: any, index: number) => (
                                    <ul key={index}>
                                        <li>{list}</li>
                                    </ul>
                                ))}
                            </StyledWrapper>
                        }
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ProductDetailsList)),
    definition: {
        displayName: " Custom Product Details List",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title ",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
        ],
    },
};

export default widgetModule;
